// Shared time-range filter used by Home's Money + Activity sections.

export type TimeRange = '30d' | '60d' | '6mo' | '1yr'

export interface TimeRangeOption {
  value: TimeRange
  days: number
  label: string       // "30 days", "6 months"
  shortLabel: string  // "30d", "6mo"
}

export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { value: '30d', days: 30,  label: '30 days',  shortLabel: '30d' },
  { value: '60d', days: 60,  label: '60 days',  shortLabel: '60d' },
  { value: '6mo', days: 183, label: '6 months', shortLabel: '6mo' },
  { value: '1yr', days: 365, label: '1 year',   shortLabel: '1yr' },
]

export function timeRangeDays(r: TimeRange): number {
  return TIME_RANGE_OPTIONS.find((o) => o.value === r)?.days ?? 30
}

export function timeRangeLabel(r: TimeRange): string {
  return TIME_RANGE_OPTIONS.find((o) => o.value === r)?.label ?? '30 days'
}

// Cutoff date (inclusive). Dates >= cutoff are "in range."
export function rangeCutoffDate(r: TimeRange): Date {
  const cutoff = new Date()
  cutoff.setHours(0, 0, 0, 0)
  cutoff.setDate(cutoff.getDate() - timeRangeDays(r))
  return cutoff
}

/** Local-midnight cutoff expressed as 'YYYY-MM-DD' for direct string compare. */
function rangeCutoffIso(r: TimeRange): string {
  const d = rangeCutoffDate(r)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Compare ISO date strings directly. `new Date('YYYY-MM-DD')` parses as UTC
 * midnight, then comparing to a local-midnight cutoff drifts by the timezone
 * offset — so sales on boundary dates flicker in/out for users east of UTC.
 * Comparing 'YYYY-MM-DD' strings lexicographically avoids the round-trip
 * through Date entirely.
 */
export function isWithinRange(isoDate: string, range: TimeRange): boolean {
  return isoDate >= rangeCutoffIso(range)
}

// Sparkline bucketing — how many bars to draw and how wide each bar is.
export interface BucketShape {
  bucketDays: number
  numBuckets: number
}

export function bucketShape(r: TimeRange): BucketShape {
  switch (r) {
    case '30d': return { bucketDays: 1,  numBuckets: 30 }
    case '60d': return { bucketDays: 1,  numBuckets: 60 }
    case '6mo': return { bucketDays: 7,  numBuckets: 26 }
    case '1yr': return { bucketDays: 30, numBuckets: 12 }
  }
}
